/**
 * FormBuilderPage — admin page for creating / editing forms.
 *
 * Routes:
 *   /setup/forms/new         — fresh form
 *   /setup/forms/:id         — edit existing form
 *
 * Two tabs:
 *   "Edit"     — three-pane FormBuilder
 *   "Preview"  — live FormRenderer rendering the in-progress schema
 *
 * Save → POST /api/forms (new) or PATCH /api/forms/:id (edit).
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import FormBuilder from './FormBuilder';
import FormRenderer from './FormRenderer';

const COLORS = {
  brand: '#3F66A0', brandTint: '#eef3fb',
  border: '#e2e8f0', ink: '#0f172a', inkSoft: '#475569', inkMuted: '#94a3b8',
};

const EMPTY_SCHEMA = {
  version: 1,
  title: '',
  sections: [{ title: 'Section 1', description: '', fields: [] }],
};

export default function FormBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [schema, setSchema] = useState(EMPTY_SCHEMA);
  const [tab, setTab] = useState('edit');
  const [previewValues, setPreviewValues] = useState({});
  const [previewFiles, setPreviewFiles] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    api.getForm(id)
      .then((r) => {
        if (cancelled) return;
        setName(r.form.name || '');
        setDescription(r.form.description || '');
        setSchema(r.form.schema || EMPTY_SCHEMA);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    return () => { cancelled = true; };
  }, [id, isNew]);

  const save = async () => {
    if (!name.trim()) return setError('Name is required');
    setError('');
    setSaving(true);
    try {
      if (isNew) {
        const r = await api.createForm({ name, description, schema });
        navigate(`/setup/forms/${r.id}`, { replace: true });
      } else {
        await api.updateForm(id, { name, description, schema });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page" style={{ padding: 24 }}>
        <div style={{ color: COLORS.inkMuted }}>Loading form…</div>
      </div>
    );
  }

  return (
    <div className="page" style={{ padding: '28px 32px 48px', maxWidth: 1280, margin: '0 auto' }}>
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap', marginBottom: 18,
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: COLORS.ink, letterSpacing: '-0.02em' }}>
            {isNew ? 'New Form' : 'Edit Form'}
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: COLORS.inkMuted }}>
            Drag-build the inflow form for a workflow. Preview tab shows it the way users will see it.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" onClick={() => navigate(-1)}
            style={{ padding: '10px 18px', borderRadius: 10, border: `1.5px solid ${COLORS.border}`,
              background: '#fff', color: COLORS.inkSoft, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Cancel
          </button>
          <button type="button" onClick={save} disabled={saving}
            style={{ padding: '10px 22px', borderRadius: 10, border: 'none',
              background: saving ? '#cbd5e1' : COLORS.brand, color: '#fff',
              fontSize: 14, fontWeight: 700, cursor: saving ? 'wait' : 'pointer',
              boxShadow: saving ? 'none' : '0 2px 6px rgba(63,102,160,0.25)' }}>
            {saving ? 'Saving…' : 'Save Form'}
          </button>
        </div>
      </header>

      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14,
        marginBottom: 16,
      }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: COLORS.inkSoft,
            marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Form Name (admin)</label>
          <input
            type="text"
            value={name}
            placeholder="e.g. Property Site Inspection Request"
            onChange={(e) => setName(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', fontSize: 14, padding: '10px 12px',
              border: `1.5px solid ${COLORS.border}`, borderRadius: 8, outline: 'none' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: COLORS.inkSoft,
            marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Description (admin)</label>
          <input
            type="text"
            value={description}
            placeholder="Internal description for other admins"
            onChange={(e) => setDescription(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', fontSize: 14, padding: '10px 12px',
              border: `1.5px solid ${COLORS.border}`, borderRadius: 8, outline: 'none' }}
          />
        </div>
      </div>

      {/* Tab strip */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[
          { key: 'edit', label: 'Edit' },
          { key: 'preview', label: 'Preview' },
        ].map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              style={{
                padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 700,
                border: `1.5px solid ${active ? COLORS.brand : COLORS.border}`,
                background: active ? COLORS.brandTint : '#fff',
                color: active ? COLORS.brand : COLORS.inkSoft,
                cursor: 'pointer',
              }}
            >{t.label}</button>
          );
        })}
      </div>

      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: 10, marginBottom: 14,
          background: '#fef3c7', border: '1px solid #fcd34d',
          color: '#78350f', fontSize: 13, fontWeight: 600,
        }}>{error}</div>
      )}

      {tab === 'edit' && (
        <FormBuilder value={schema} onChange={setSchema} />
      )}

      {tab === 'preview' && (
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          {(schema.title || schema.sections.some(s => s.fields.length > 0)) ? (
            <>
              {schema.title && (
                <h2 style={{ margin: '0 0 18px', fontSize: 22, fontWeight: 800, color: COLORS.ink }}>
                  {schema.title}
                </h2>
              )}
              <FormRenderer
                schema={schema}
                values={previewValues}
                onChange={setPreviewValues}
                files={previewFiles}
                onFilesChange={setPreviewFiles}
                submitLabel="Submit (preview only)"
                onSubmit={async () => {
                  alert('Preview only — submit isn\'t wired in builder mode. Save the form, then submit it from /forms/' + (id || '<id>') + '/submit.');
                }}
              />
            </>
          ) : (
            <div style={{
              padding: 32, borderRadius: 12, border: `1px dashed ${COLORS.border}`,
              textAlign: 'center', color: COLORS.inkMuted,
            }}>
              Add a title and a few fields on the Edit tab — they'll appear here as your users will see them.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
